import { reactive } from 'vue'

const STORAGE_KEY = 'mp_tenant_receipt'
const CURRENT_ADDRESS = '\u6d59\u6c5f\u7701\u91d1\u534e\u5e02\u6b66\u4e49\u53bf\u79e6\u4f59\u8def8-5\u53f7'
const CURRENT_PHONES = ['18267096196']
const LEGACY_ADDRESS_MARKERS = [
  '\u91d1\u51ef\u7269\u6d41\u56ed',
  '\u6850\u7434\u9547',
]
const LEGACY_PHONES = new Set(['0579-87654321', '138-0000-0000'])
const RECEIPT_FONT_DEFAULTS = {
  fontSize: 13,
  titleFontSize: 25,
  metaFontSize: 15,
  tableFontSize: 15,
  footerFontSize: 15,
}
const PREVIOUS_SMALL_RECEIPT_FONT_DEFAULTS = {
  fontSize: 12,
  titleFontSize: 20,
  metaFontSize: 12,
  tableFontSize: 12,
  footerFontSize: 12,
}
const RECEIPT_ROW_HEIGHT_DEFAULT = 26
const PREVIOUS_RECEIPT_ROW_HEIGHT_DEFAULT = 26
const PREVIOUS_LARGE_RECEIPT_ROW_HEIGHT_DEFAULT = 32
const RECEIPT_MARGIN_DEFAULTS = {
  top: 3.8,
  right: 7.95,
  bottom: 2,
  left: 7.95,
}

const defaults = {
  /** 左侧侧边栏品牌：主标题（如：武义箭冠） */
  brandTitle: '武义箭冠',
  /** 左侧侧边栏品牌：副标题（如：汽配门店管理后台） */
  brandSubtitle: '汽配门店管理后台',
  /** 销售单标题（如：武义箭冠汽配销售单） */
  receiptTitle: '武义箭冠汽配销售单',
  /** 订货单收货方（发给供应商时展示“卖给谁/收货方”） */
  orderBuyerName: '武义箭冠',
  /** 兼容历史字段：老版本用 shopName 作为销售单标题 */
  shopName: '武义箭冠汽配销售单',
  address: CURRENT_ADDRESS,
  phones: [...CURRENT_PHONES],
  printOffset: { ...RECEIPT_MARGIN_DEFAULTS },
  /** 打印样式设计（仅前端预览/套打微调） */
  receiptDesign: {
    fontSize: RECEIPT_FONT_DEFAULTS.fontSize,   // px, legacy base font size
    titleFontSize: RECEIPT_FONT_DEFAULTS.titleFontSize, // px
    metaFontSize: RECEIPT_FONT_DEFAULTS.metaFontSize,  // px, customer / document no / date
    tableFontSize: RECEIPT_FONT_DEFAULTS.tableFontSize, // px, item list
    footerFontSize: RECEIPT_FONT_DEFAULTS.footerFontSize, // px, totals / address / phone
    rowHeight: RECEIPT_ROW_HEIGHT_DEFAULT,  // px
    itemRowsPerPage: 7,
    codeType: 'qrcode',
    qrSize: 18,     // mm
    qrTop: 1.2,     // mm
    qrLeft: 50,     // percent
    showQrCode: false,
    showSignArea: false,
    showPaySummary: false,
    receiptNote: '感谢惠顾，祝您一路平安！',
    showCols: {
      partId: true,
      partName: true,
      spec: true,
      unit: true,
      price: true,
      quantity: true,
      amount: true,
    },
  },
}

function normalizeReceiptDesignFontDefaults(design = {}) {
  const next = { ...(design || {}) }
  const hasRowsPerPage = next.itemRowsPerPage !== undefined && next.itemRowsPerPage !== null && next.itemRowsPerPage !== ''
  if (!hasRowsPerPage && Number(next.rowHeight) === PREVIOUS_LARGE_RECEIPT_ROW_HEIGHT_DEFAULT) {
    next.rowHeight = RECEIPT_ROW_HEIGHT_DEFAULT
  }
  const splitFontKeys = ['titleFontSize', 'metaFontSize', 'tableFontSize', 'footerFontSize']
  const hasSplitFontValue = splitFontKeys.some((key) => next[key] !== undefined && next[key] !== null && next[key] !== '')
  const fontSizeLooksLikeSmallDefault = Number(next.fontSize || PREVIOUS_SMALL_RECEIPT_FONT_DEFAULTS.fontSize) === PREVIOUS_SMALL_RECEIPT_FONT_DEFAULTS.fontSize
  const splitFontsLookLikeSmallDefault = splitFontKeys.every((key) => (
    next[key] === undefined ||
    next[key] === null ||
    next[key] === '' ||
    Number(next[key]) === PREVIOUS_SMALL_RECEIPT_FONT_DEFAULTS[key]
  ))
  if ((!hasSplitFontValue && fontSizeLooksLikeSmallDefault) || (fontSizeLooksLikeSmallDefault && splitFontsLookLikeSmallDefault)) {
    const rowHeight = next.rowHeight === undefined || next.rowHeight === null || Number(next.rowHeight) === PREVIOUS_RECEIPT_ROW_HEIGHT_DEFAULT
      ? RECEIPT_ROW_HEIGHT_DEFAULT
      : next.rowHeight
    return { ...next, ...RECEIPT_FONT_DEFAULTS, rowHeight }
  }
  return next
}

function migrateLegacyContact(o) {
  const next = { ...(o || {}) }
  const address = String(next.address || '').trim()
  const phones = Array.isArray(next.phones) ? next.phones.map(p => String(p || '').trim()).filter(Boolean) : []
  const hasLegacyAddress = !address || LEGACY_ADDRESS_MARKERS.some(marker => address.includes(marker))
  const hasOnlyLegacyPhones = phones.length > 0 && phones.every(phone => LEGACY_PHONES.has(phone))
  if (hasLegacyAddress) next.address = CURRENT_ADDRESS
  if (!phones.length || hasOnlyLegacyPhones) next.phones = [...CURRENT_PHONES]
  return next
}

function normalizePrintMargins(value = {}) {
  const raw = value && typeof value === 'object' ? value : {}
  const legacyOffsetShape = raw.right === undefined && raw.bottom === undefined
  const numberOr = (input, fallback) => {
    const n = Number(input)
    return Number.isFinite(n) ? n : fallback
  }
  const clampMargin = (input) => Math.max(0, Math.min(20, Number(input.toFixed(2))))
  return {
    top: clampMargin(legacyOffsetShape
      ? RECEIPT_MARGIN_DEFAULTS.top + numberOr(raw.top, 0)
      : numberOr(raw.top, RECEIPT_MARGIN_DEFAULTS.top)),
    right: clampMargin(numberOr(raw.right, RECEIPT_MARGIN_DEFAULTS.right)),
    bottom: clampMargin(numberOr(raw.bottom, RECEIPT_MARGIN_DEFAULTS.bottom)),
    left: clampMargin(legacyOffsetShape
      ? RECEIPT_MARGIN_DEFAULTS.left + numberOr(raw.left, 0)
      : numberOr(raw.left, RECEIPT_MARGIN_DEFAULTS.left)),
  }
}

function mergeTenantConfig(value = {}) {
  const o = migrateLegacyContact(value)
  // 兼容：旧配置只存了 shopName，用它回填 receiptTitle
  const receiptTitle = String(o.receiptTitle || '').trim() || String(o.shopName || '').trim() || defaults.receiptTitle
  const orderBuyerName = String(o.orderBuyerName || '').trim() || defaults.orderBuyerName
  const brandTitle = String(o.brandTitle || '').trim() || defaults.brandTitle
  const brandSubtitle = String(o.brandSubtitle || '').trim() || defaults.brandSubtitle
  const design = normalizeReceiptDesignFontDefaults(o.receiptDesign || {})
  const showCols = (design && design.showCols) || {}
  const baseFontSize = Number(design.fontSize || defaults.receiptDesign.fontSize)
  return {
    ...defaults,
    ...o,
    brandTitle,
    brandSubtitle,
    receiptTitle,
    orderBuyerName,
    address: String(o.address || '').trim() || defaults.address,
    phones: Array.isArray(o.phones) && o.phones.length ? [...o.phones] : [...defaults.phones],
    printOffset: normalizePrintMargins(o.printOffset),
    receiptDesign: {
      ...defaults.receiptDesign,
      ...design,
      titleFontSize: Number(design.titleFontSize || defaults.receiptDesign.titleFontSize),
      metaFontSize: Number(design.metaFontSize || Math.max(defaults.receiptDesign.metaFontSize, baseFontSize + 1)),
      tableFontSize: Number(design.tableFontSize || Math.max(defaults.receiptDesign.tableFontSize, baseFontSize)),
      footerFontSize: Number(design.footerFontSize || Math.max(defaults.receiptDesign.footerFontSize, baseFontSize)),
      rowHeight: Math.max(18, Math.min(50, Number(design.rowHeight || defaults.receiptDesign.rowHeight))),
      itemRowsPerPage: Math.max(3, Math.min(10, Math.floor(Number(design.itemRowsPerPage || defaults.receiptDesign.itemRowsPerPage)))),
      codeType: design.codeType === 'barcode' ? 'barcode' : 'qrcode',
      showCols: { ...defaults.receiptDesign.showCols, ...showCols },
    },
  }
}

function loadMerged() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return mergeTenantConfig(raw ? JSON.parse(raw) : {})
  } catch {
    return mergeTenantConfig({})
  }
}

/** 品牌（侧边栏）、销售单抬头/页脚、打印偏移（可在「系统设置」中修改并持久化） */
export const tenantConfig = reactive(loadMerged())

export function getTenantConfigSnapshot() {
  return {
    brandTitle: tenantConfig.brandTitle,
    brandSubtitle: tenantConfig.brandSubtitle,
    receiptTitle: tenantConfig.receiptTitle,
    orderBuyerName: tenantConfig.orderBuyerName,
    shopName: tenantConfig.receiptTitle,
    address: tenantConfig.address,
    phones: [...(tenantConfig.phones || [])],
    printOffset: { ...(tenantConfig.printOffset || {}) },
    receiptDesign: {
      ...(tenantConfig.receiptDesign || {}),
      showCols: { ...(tenantConfig.receiptDesign?.showCols || {}) },
    },
  }
}

export function getReceiptPrintSettingsSnapshot() {
  return {
    printOffset: { ...(tenantConfig.printOffset || {}) },
    receiptDesign: {
      ...(tenantConfig.receiptDesign || {}),
      showCols: { ...(tenantConfig.receiptDesign?.showCols || {}) },
    },
  }
}

export function saveTenantConfig() {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(getTenantConfigSnapshot()))
}

export function applyTenantConfig(value, persist = true) {
  if (!value || typeof value !== 'object' || Array.isArray(value) || !Object.keys(value).length) return false
  const current = getTenantConfigSnapshot()
  const incomingPrintMargins = value.printOffset ? normalizePrintMargins(value.printOffset) : {}
  const mergedValue = {
    ...current,
    ...value,
    printOffset: { ...current.printOffset, ...incomingPrintMargins },
    receiptDesign: {
      ...current.receiptDesign,
      ...(value.receiptDesign || {}),
      showCols: {
        ...(current.receiptDesign?.showCols || {}),
        ...(value.receiptDesign?.showCols || {}),
      },
    },
  }
  Object.assign(tenantConfig, mergeTenantConfig(mergedValue))
  if (persist) saveTenantConfig()
  return true
}
